import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  ExpressCheckoutElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { Loader2, Check, Shield, CreditCard, X, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config/api';

const API = API_URL;

// Initialize Stripe
let stripePromise = null;

const getStripe = async () => {
  if (!stripePromise) {
    const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51QczwmP8SCy20iNJkdVLRyEJAFAMJUYsaS3WkV21FLdYHOqO8jAD8sXB7mYgZWQ8KoWxQkKbKvxZfPz8xYqZmZyP00vqZkPwFx';
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
};

// Payment Form Component
function CheckoutForm({ planType, amount, months, onSuccess, onCancel, clientSecret, t, currencySymbol = '€' }) {
  const stripe = useStripe();
  const elements = useElements();
  const { isDark } = useTheme();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [expressCheckoutReady, setExpressCheckoutReady] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements || !isReady) {
      setError(t('payment.waitLoading'));
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const { error: submitError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/premium/success`,
        },
        redirect: 'if_required'
      });

      if (submitError) {
        setError(submitError.message);
        setIsProcessing(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        try {
          await axios.post(
            `${API}/api/premium/confirm-payment`,
            null,
            { 
              params: { payment_intent_id: paymentIntent.id },
              withCredentials: true 
            }
          );
          onSuccess();
        } catch (confirmErr) {
          console.error('Error confirming payment:', confirmErr);
          onSuccess();
        }
      }
    } catch (err) {
      setError(err.message || t('payment.paymentError'));
      setIsProcessing(false);
    }
  };

  // Handle Express Checkout (Apple Pay / Google Pay)
  const onExpressCheckoutConfirm = async (event) => {
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setError(null);

    const { error: submitError, paymentIntent } = await stripe.confirmPayment({
      elements,
      clientSecret,
      confirmParams: {
        return_url: `${window.location.origin}/premium/success`,
      },
      redirect: 'if_required'
    });

    if (submitError) {
      setError(submitError.message);
      setIsProcessing(false);
      return;
    }

    if (paymentIntent && paymentIntent.status === 'succeeded') {
      try {
        await axios.post(
          `${API}/api/premium/confirm-payment`,
          null,
          { 
            params: { payment_intent_id: paymentIntent.id },
            withCredentials: true 
          }
        );
        onSuccess();
      } catch (confirmErr) {
        onSuccess();
      }
    }
  };

  return (
    <div className={`fixed inset-0 flex flex-col ${isDark ? 'bg-[#0A0F1C]' : 'bg-white'}`}>
      {/* Fixed Header with Safe Area */}
      <div 
        className={`flex-shrink-0 px-4 py-3 ${isDark ? 'bg-[#0A0F1C]' : 'bg-white'} border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            className={`p-2 -ml-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
          >
            <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-900'}`} />
          </button>
          <h1 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('payment.completePayment')}
          </h1>
        </div>
      </div>

      {/* Scrollable Content - leave space for fixed button at bottom */}
      <div 
        className="flex-1 overflow-y-auto"
        style={{ paddingBottom: 'calc(80px + env(safe-area-inset-bottom, 0px))' }}
      >
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Order Summary - Compact */}
          <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'}`}>
            <div className="flex justify-between items-center">
              <div>
                <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {planType === 'monthly' ? t('payment.monthlyPlan') : t('payment.annualPlan')}
                </p>
                <p className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  {months === 1 ? t('payment.monthAccess') : t('payment.monthsAccess', { count: months })}
                </p>
              </div>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                €{amount.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Express Checkout (Apple Pay / Google Pay) */}
          <div className={`rounded-xl overflow-hidden`}>
            <ExpressCheckoutElement
              onConfirm={onExpressCheckoutConfirm}
              onReady={() => setExpressCheckoutReady(true)}
              options={{
                buttonType: {
                  applePay: 'buy',
                  googlePay: 'buy'
                },
                buttonTheme: {
                  applePay: isDark ? 'white' : 'black',
                  googlePay: isDark ? 'white' : 'black'
                },
                layout: {
                  maxColumns: 2,
                  maxRows: 1
                }
              }}
            />
          </div>

          {/* Divider */}
          {expressCheckoutReady && (
            <div className="flex items-center gap-3">
              <div className={`flex-1 h-px ${isDark ? 'bg-white/20' : 'bg-gray-300'}`} />
              <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('payment.orPayWithCard')}
              </span>
              <div className={`flex-1 h-px ${isDark ? 'bg-white/20' : 'bg-gray-300'}`} />
            </div>
          )}

          {/* Payment Element (Card) - Compact */}
          <div className={`rounded-xl ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'}`}>
            <PaymentElement 
              onReady={() => setIsReady(true)}
              options={{
                layout: {
                  type: 'accordion',
                  defaultCollapsed: false,
                  radios: 'never',
                  spacedAccordionItems: false
                },
                paymentMethodOrder: ['card', 'klarna'],
                defaultValues: {
                  billingDetails: {
                    name: '',
                  }
                }
              }}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 rounded-xl bg-red-500/20 border border-red-500/50">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Security Badge - Compact */}
          <div className="flex items-center justify-center gap-2">
            <Shield className={`w-3 h-3 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
            <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
              {t('payment.securePayment')}
            </span>
          </div>
        </form>
      </div>

      {/* FIXED Bottom Button - anchored to very bottom */}
      <div 
        className={`fixed left-0 right-0 px-4 pt-3 ${isDark ? 'bg-[#0A0F1C]' : 'bg-white'} border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}
        style={{ 
          bottom: 0,
          paddingBottom: '8px'
        }}
      >
        <button
          type="submit"
          onClick={handleSubmit}
          disabled={!stripe || !isReady || isProcessing}
          className="w-full py-3.5 px-6 rounded-xl font-semibold bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {t('payment.processing')}
            </>
          ) : !isReady ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {t('payment.loading')}
            </>
          ) : (
            <>
              <CreditCard className="w-5 h-5" />
              {t('payment.pay')} {currencySymbol}{amount.toFixed(2)}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// Main Payment Page Component (Full Screen)
export default function StripePaymentModal({ 
  isOpen, 
  onClose, 
  planType, 
  referralCode,
  onSuccess,
  currency = 'eur',
  currencySymbol = '€',
  price = 0
}) {
  const { isDark } = useTheme();
  const { t, language } = useLanguage();
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stripeLoaded, setStripeLoaded] = useState(null);

  useEffect(() => {
    getStripe().then(setStripeLoaded);
  }, []);

  // Disable body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    };
  }, [isOpen]);

  const initializePayment = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await axios.post(
        `${API}/api/premium/create-payment-intent`,
        { plan_type: planType, referral_code: referralCode, currency: currency },
        { withCredentials: true }
      );
      
      setClientSecret(res.data.client_secret);
      setPaymentDetails({
        amount: res.data.amount,
        planType: res.data.plan_type,
        months: res.data.months,
        currency: res.data.currency || currency,
        currencySymbol: res.data.currency === 'usd' ? '$' : '€'
      });
    } catch (err) {
      console.error('Error creating payment intent:', err);
      setError(err.response?.data?.detail || t('payment.createPaymentError'));
    } finally {
      setLoading(false);
    }
  }, [planType, referralCode, currency, t]);

  // Initialize payment when modal opens
  useEffect(() => {
    if (isOpen && planType) {
      initializePayment();
    }
  }, [isOpen, planType, initializePayment]);

  const handleSuccess = () => {
    onSuccess();
    onClose();
  };

  if (!isOpen) return null;

  const appearance = {
    theme: isDark ? 'night' : 'stripe',
    variables: {
      colorPrimary: '#F59E0B',
      colorBackground: isDark ? '#0A0F1C' : '#ffffff',
      colorText: isDark ? '#ffffff' : '#1a1a2e',
      colorDanger: '#ef4444',
      fontFamily: 'Inter, system-ui, sans-serif',
      borderRadius: '10px',
      spacingUnit: '3px',
      fontSizeBase: '14px'
    },
    rules: {
      '.Input': {
        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb',
        border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb',
        padding: '10px 12px',
        fontSize: '14px'
      },
      '.Input:focus': {
        border: '2px solid #F59E0B',
        boxShadow: 'none'
      },
      '.Label': {
        color: isDark ? 'rgba(255,255,255,0.7)' : '#374151',
        fontWeight: '500',
        fontSize: '13px',
        marginBottom: '4px'
      },
      '.Tab': {
        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb',
        border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb',
        padding: '8px 12px',
        fontSize: '13px'
      },
      '.Tab--selected': {
        backgroundColor: isDark ? 'rgba(245,158,11,0.2)' : '#fef3c7',
        borderColor: '#F59E0B'
      },
      '.TabIcon': {
        marginRight: '6px'
      },
      '.AccordionItem': {
        borderBottom: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb'
      },
      '.Block': {
        padding: '12px'
      }
    }
  };

  // Full screen portal - covers entire viewport including safe areas
  return createPortal(
    <div 
      className="payment-modal-fullscreen"
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0,
        zIndex: 9999,
        backgroundColor: isDark ? '#0A0F1C' : '#ffffff',
      }}
    >
      {loading ? (
        <div className={`w-full h-full flex flex-col items-center justify-center ${isDark ? 'bg-[#0A0F1C]' : 'bg-white'}`}>
          <Loader2 className={`w-10 h-10 animate-spin ${isDark ? 'text-white' : 'text-gray-900'}`} />
          <p className={`mt-4 text-lg ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
            {t('payment.preparingPayment')}
          </p>
        </div>
      ) : error ? (
        <div className={`w-full h-full flex flex-col items-center justify-center p-6 ${isDark ? 'bg-[#0A0F1C]' : 'bg-white'}`}>
          <div className="w-20 h-20 mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
            <X className="w-10 h-10 text-red-500" />
          </div>
          <p className={`text-lg text-center mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>{error}</p>
          <div className="flex gap-4">
            <button
              onClick={onClose}
              className={`px-6 py-3 rounded-xl font-medium ${isDark ? 'bg-white/10 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={initializePayment}
              className="px-6 py-3 rounded-xl bg-[#E8334A] text-white font-medium"
            >
              {t('payment.retry')}
            </button>
          </div>
        </div>
      ) : clientSecret && stripeLoaded && paymentDetails ? (
        <Elements 
          stripe={stripeLoaded} 
          options={{ 
            clientSecret,
            appearance,
            locale: language === 'it' ? 'it' : language === 'es' ? 'es' : language === 'pt' ? 'pt-BR' : language === 'de' ? 'de' : language === 'fr' ? 'fr' : 'en'
          }}
        >
          <CheckoutForm
            planType={paymentDetails.planType}
            amount={paymentDetails.amount}
            months={paymentDetails.months}
            onSuccess={handleSuccess}
            onCancel={onClose}
            clientSecret={clientSecret}
            t={t}
            currencySymbol={paymentDetails.currencySymbol || currencySymbol}
          />
        </Elements>
      ) : (
        <div className={`w-full h-full flex items-center justify-center ${isDark ? 'bg-[#0A0F1C]' : 'bg-white'}`}>
          <Loader2 className={`w-10 h-10 animate-spin ${isDark ? 'text-white' : 'text-gray-900'}`} />
        </div>
      )}
    </div>,
    document.body
  );
}
